import React, { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const Field = ({ label, ...props }) => (
  <div className="space-y-1.5">
    <Label className="text-base font-semibold">{label}</Label>
    <Input {...props} className="min-h-[54px] rounded-xl border-2 border-stone-300 px-4 text-lg" />
  </div>
);

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t, lang, setLang } = useLang();
  const s = user?.socials || {};
  const [form, setForm] = useState({
    first_name: user?.first_name || "", display_name: user?.display_name || "",
    instagram: s.instagram || "", tiktok: s.tiktok || "", facebook: s.facebook || "", youtube: s.youtube || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/auth/profile", {
        first_name: form.first_name, display_name: form.display_name, language: lang,
        socials: { instagram: form.instagram, tiktok: form.tiktok, facebook: form.facebook, youtube: form.youtube },
      });
      setUser(data);
      toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail, t("err_generic"))); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-head font-black text-3xl sm:text-4xl mb-8">{t("profile_title")}</h1>
      <div className="bg-white rounded-3xl border-2 border-stone-200 p-6 sm:p-8 space-y-5" data-testid="profile-form">
        <Field label={t("first_name")} value={form.first_name} onChange={set("first_name")} data-testid="profile-firstname" />
        <Field label={t("display_name")} value={form.display_name} onChange={set("display_name")} />
        <div>
          <Label className="text-base font-semibold">{t("email")}</Label>
          <Input value={user?.email} disabled className="min-h-[54px] rounded-xl border-2 border-stone-200 px-4 text-lg bg-stone-100 text-stone-700 disabled:opacity-100 mt-1.5" />
        </div>
        <div>
          <Label className="text-base font-semibold">{t("lang_q")}</Label>
          <div className="flex gap-3 mt-1.5">
            {["fr", "en"].map((l) => (
              <button key={l} type="button" onClick={() => setLang(l)} data-testid={`profile-lang-${l}`}
                className={`flex-1 min-h-[52px] rounded-xl border-2 font-bold transition-colors ${lang === l ? "border-primary bg-primary/10 text-primary" : "border-stone-200"}`}>
                {l === "fr" ? "🇫🇷 Français" : "🇬🇧 English"}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Instagram" value={form.instagram} onChange={set("instagram")} />
          <Field label="TikTok" value={form.tiktok} onChange={set("tiktok")} />
          <Field label="Facebook" value={form.facebook} onChange={set("facebook")} />
          <Field label="YouTube" value={form.youtube} onChange={set("youtube")} />
        </div>
        <Button onClick={save} disabled={busy} data-testid="profile-save" className="w-full rounded-full bg-primary hover:bg-[#6D28D9] min-h-[56px] font-bold text-lg">
          {busy ? t("loading") : t("save")}
        </Button>
      </div>
    </div>
  );
}
